// publicCouponCode.js — pure helpers for the "public shared code" option on
// BO → App Catalog → คูปอง (T-014 / asrth/mdf-www#372, spec §10.4).
//
// A coupon_template with eligibility = "public_code" is a code that many
// members type at www checkout (e.g. FIN100). The server (bo-api
// BOCatalogCouponTemplateCreate/Update) validates the same rules; keeping them
// here in one pure function means the form and the server-error mapper are
// checked against one source, and — because this repo cannot mount .vue files
// in jest (memory: reference-bo-fn-jest-infra) — it is the part a unit test
// can reach.
//
// NOTHING IN THIS FILE MAY IMPORT VUE, VUEX OR vue-i18n.

export const PUBLIC_CODE_ELIGIBILITY = "public_code";

// Default total quota suggested when an operator switches a template to a
// public code (§10.5: 0 means unlimited, which is a liability for a code
// anyone can type).
export const PUBLIC_CODE_DEFAULT_QUOTA = 100;

// Same pattern the server enforces: ^[A-Z0-9]{4,20}$ after trim + upper.
export const PUBLIC_CODE_PATTERN = /^[A-Z0-9]{4,20}$/;

export function isPublicCode(eligibility) {
	return eligibility === PUBLIC_CODE_ELIGIBILITY;
}

// normalizePublicCode — what the input shows while typing: upper case, no
// surrounding spaces. Non-alphanumerics are left in place so the operator sees
// the refusal instead of the form silently rewriting their code.
export function normalizePublicCode(value) {
	if (value === null || value === undefined) return "";
	return String(value).trim().toUpperCase();
}

// onEligibilityChange — the form patch to apply when the eligibility select
// changes. Returns a NEW object; never mutates the input.
export function onEligibilityChange(form, eligibility) {
	const next = { ...form, eligibility };
	if (!isPublicCode(eligibility)) return next;
	next.code = normalizePublicCode(form.code);
	if (!Number(form.total_quota)) next.total_quota = PUBLIC_CODE_DEFAULT_QUOTA;
	return next;
}

// validatePublicCodeForm — i18n keys (under app_catalog.*) for each rule the
// form breaks. Empty array = OK. Non-public templates always pass here (the
// regular el-form rules still apply to them).
export function validatePublicCodeForm(form) {
	if (!form || !isPublicCode(form.eligibility)) return [];
	const errs = [];
	if (!PUBLIC_CODE_PATTERN.test(normalizePublicCode(form.code))) {
		errs.push("app_catalog.err_public_code_invalid");
	}
	if (!(Number(form.expiry_days) >= 1)) {
		errs.push("app_catalog.err_public_code_expiry_days");
	}
	if (form.discount_type !== "shipping_free" && !(Number(form.discount_value) > 0)) {
		errs.push("app_catalog.err_public_code_discount_value");
	}
	if (Number(form.points_cost) > 0) {
		errs.push("app_catalog.err_public_code_points_cost");
	}
	if (form.valid_from && form.valid_to) {
		const from = new Date(String(form.valid_from).replace(" ", "T")).getTime();
		const to = new Date(String(form.valid_to).replace(" ", "T")).getTime();
		if (!isNaN(from) && !isNaN(to) && to <= from) {
			errs.push("app_catalog.err_public_code_valid_window");
		}
	}
	return errs;
}

// issuedQuotaLabel — list column "ใช้ไป/โควตา": total_issued / total_quota,
// with quota 0 shown as ∞ (unlimited).
export function issuedQuotaLabel(row) {
	const r = row || {};
	const issued = Number(r.total_issued) || 0;
	const quota = Number(r.total_quota) || 0;
	return `${issued}/${quota > 0 ? quota : "∞"}`;
}

// Server refusal codes for public templates (bo-api §10.4 contract): bodies
// are {Status:"error", Msg:"errors.catalog.<code>", code:"<code>"}. `code` is
// checked first, then Msg (with or without the errors.catalog. prefix).
export const PUBLIC_CODE_ERROR_KEYS = {
	public_code_invalid: "app_catalog.err_public_code_invalid",
	public_code_discount_required: "app_catalog.err_public_code_discount_value",
	public_code_expiry_required: "app_catalog.err_public_code_expiry_days",
	public_code_valid_range: "app_catalog.err_public_code_valid_window",
	public_code_points_cost: "app_catalog.err_public_code_points_cost",
	public_code_taken: "app_catalog.err_public_code_taken",
	public_code_conflicts_campaign: "app_catalog.err_public_code_conflicts_campaign",
};

// publicCodeErrorKey — the i18n key for a known server refusal, or null so the
// caller falls back to the generic apiErrorMessage.
export function publicCodeErrorKey(err) {
	const data = err && err.response && err.response.data;
	if (!data) return null;
	const candidates = [data.code, data.Code, data.Msg, data.message];
	for (const c of candidates) {
		if (typeof c !== "string") continue;
		const bare = c.trim().replace(/^errors\.catalog\./, "").replace(/:\s*$/, "");
		const k = Object.prototype.hasOwnProperty.call(PUBLIC_CODE_ERROR_KEYS, bare) ? PUBLIC_CODE_ERROR_KEYS[bare] : null;
		if (k) return k;
	}
	return null;
}
